import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import TextStyle from '@tiptap/extension-text-style';
import Color from '@tiptap/extension-color';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import { useStore } from '../../store';
import { api } from '../../utils/api';
import { fmtBkk } from '../../utils/bkkTime';
import { joinOutgoingBody, splitOutgoingBody } from '../../utils/compose';
import { menuPill, pageToolbar, rowTint, PAGE_INSET } from '../../tabPalette';
import { HtmlFrame, fileIcon } from '../EmailDetail';
import EmailTagInput from '../EmailTagInput';
import { TEXT_COLORS, IndentParagraph, shiftIndent } from '../RichTextEditor';
import Pagination from '../Pagination';

// ── Emails waiting on a supervisor's check (report 4aaa018b) ───────────────────────────────────────────────
// What the Approvals button in the Email box opens: the emails this login was asked to check (server: services/emailCheck.ts), the
// whole of each one, with Approve, Edit and Send back. Approve sends it as this page shows it, from the writer's mailbox. Edit
// (opening #2) changes its addresses, subject and message first and saves them onto the waiting email, so that Approve then sends
// the edited version and Send back returns it to the writer's Drafts with the note. It stays apart from the Outbox tab.
const HOME = 'email_check';
type Row = {
  id: string; accountEmail: string; accountName: string; writer: string; to: string[]; cc: string[]; bcc: string[]; subject: string;
  attachments: { id: string; name: string; type: string; size: number }[]; bodyLang: string; targetLang: string; askedAt: number; bodyHtml?: string;
  editedByName?: string; editedAt?: number;
};
const fmtBytes = (b: number) => (b < 1024 ? `${b} B` : b < 1048576 ? `${(b / 1024).toFixed(0)} KB` : `${(b / 1048576).toFixed(1)} MB`);
const LABEL_W = 62;

const head = (label: string, value: string) => (
  <div style={{ display: 'flex', gap: 8, fontSize: 12.5, lineHeight: '18px' }}>
    <span style={{ width: LABEL_W, flexShrink: 0, color: 'var(--text-tertiary)', fontWeight: 600 }}>{label}</span>
    <span style={{ minWidth: 0, overflowWrap: 'anywhere', color: 'var(--text-primary)' }}>{value}</span>
  </div>
);
const neutralPill: React.CSSProperties = { padding: '7px 14px', borderRadius: 999, border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-secondary)', fontWeight: 600, fontSize: 12.5, cursor: 'pointer' };

// The composers' toolbar button (ComposeNew TBtn): pressed on mouse-down, so the editor keeps its selection.
function Tool({ on, active, title, icon }: { on: () => void; active?: boolean; title: string; icon: string }) {
  return (
    <button type="button" onMouseDown={e => { e.preventDefault(); on(); }} title={title} aria-label={title}
      style={{ width: 26, height: 26, borderRadius: 'var(--radius-sm)', border: 'none', background: active ? 'var(--c-gray-100)' : 'transparent', color: active ? 'var(--text-primary)' : 'var(--text-secondary)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
      <i className={`ti ${icon}`} style={{ fontSize: 13 }} aria-hidden />
    </button>
  );
}

// The supervisor's edit (opening #2: "Allow the supervisor to edit the email and send it back to the employee or if/when the email is
// satisfactory, the supervisor can release the email directly"). The addresses, the subject and the message can change; the mailbox,
// the attachments, the threading and the signature stay as they were frozen when the check was asked for. The message opens in an
// editor with the composers' own extensions, so its links, pictures, colour and indent survive, and it is spaced on Save the way a
// send spaces it (utils/compose.ts). The text goes back only once it was changed, so an address fixed alone leaves the body exactly
// as the writer's composer made it. Saving keeps the email waiting: Approve or Send back then takes this version.
function EditForm({ row, onCancel, onSaved }: { row: Row; onCancel: () => void; onSaved: (fresh: Row) => void }) {
  const { t } = useTranslation();
  const { showToast } = useStore();
  const parts = useMemo(() => splitOutgoingBody(row.bodyHtml || ''), [row.bodyHtml]);
  // What follows the message (signature, quoted email), shown under the editor; nothing when the body ends with the message.
  const kept = useMemo(() => (parts && !/^<\/div>\s*$/i.test(parts.rest) ? joinOutgoingBody(parts, '') : ''), [parts]);
  const [to, setTo] = useState<string[]>(row.to);
  const [cc, setCc] = useState<string[]>(row.cc);
  const [bcc, setBcc] = useState<string[]>(row.bcc);
  const [subject, setSubject] = useState(row.subject);
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const editor = useEditor({
    // ComposeNew's and Composer's extensions. Their pictures are pasted in as data: URLs, which Image reads back only with allowBase64.
    extensions: [StarterKit.configure({ paragraph: false }), IndentParagraph, Underline, TextStyle, Color,
      Link.configure({ openOnClick: false }), Image.configure({ allowBase64: true })],
    content: parts ? parts.message : '',
    onUpdate: () => setTouched(true),
  });
  const same = (a: string[], b: string[]) => a.length === b.length && a.every((x, i) => x === b[i]);
  const save = async () => {
    if (busy) return;
    // Nothing changed: nothing to save, and nobody is named as having edited it.
    if (!touched && subject === row.subject && same(to, row.to) && same(cc, row.cc) && same(bcc, row.bcc)) { onCancel(); return; }
    if (![...to, ...cc, ...bcc].some(a => a.trim())) { setErr(t('ec_need_recipient')); return; }
    setBusy(true); setErr('');
    try {
      const text = parts && touched && editor ? { bodyHtml: joinOutgoingBody(parts, editor.getHTML()) } : {};
      const r = await api.editEmailCheck(row.id, { to, cc, bcc, subject, ...text });
      if (r.ok === false || !r.row) { setErr(r.error || t('ec_failed')); setBusy(false); return; }
      showToast(t('ec_saved'));
      onSaved(r.row);
    } catch (e: any) { setErr(e?.message || t('ec_failed')); setBusy(false); }
  };

  const field = (label: string, control: React.ReactNode) => (
    <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', borderBottom: '1px solid var(--border)' }}>
      <span style={{ width: LABEL_W, flexShrink: 0, paddingTop: 8, fontSize: 12.5, color: 'var(--text-tertiary)', fontWeight: 600 }}>{label}</span>
      <div style={{ flex: 1, minWidth: 0 }}>{control}</div>
    </div>
  );
  const tags = (label: string, values: string[], set: React.Dispatch<React.SetStateAction<string[]>>) => field(label,
    <EmailTagInput bare label={label} values={values} onAdd={v => set(p => [...p, v])} onRemove={v => set(p => p.filter(x => x !== v))} />);
  const frame = (html: string) => (
    <div style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: 9, overflow: 'hidden' }}><HtmlFrame html={html} /></div>
  );
  const sep = <div style={{ width: 1, height: 14, background: 'var(--border)', margin: '0 2px' }} />;

  return (
    <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
        <div style={{ padding: `12px ${PAGE_INSET}px`, display: 'flex', flexDirection: 'column', gap: 3 }}>
          {head(t('ec_from'), row.accountName ? `${row.accountName} <${row.accountEmail}>` : row.accountEmail)}
          {row.targetLang && row.targetLang !== row.bodyLang && (
            <div style={{ fontSize: 11.5, color: 'var(--text-tertiary)', marginTop: 2 }}>
              <i className="ti ti-language" style={{ fontSize: 12 }} aria-hidden /> {t('ec_translated', { lang: t(`langNames.${row.targetLang}` as any, { defaultValue: row.targetLang }) })}
            </div>
          )}
          <div style={{ marginTop: 6 }}>
            {tags(t('to'), to, setTo)}
            {tags(t('ec_cc'), cc, setCc)}
            {tags(t('ec_bcc'), bcc, setBcc)}
            {field(t('subject'), <input value={subject} onChange={e => setSubject(e.target.value)} aria-label={t('subject')}
              style={{ width: '100%', boxSizing: 'border-box', border: 'none', outline: 'none', background: 'transparent', fontSize: 12.5, color: 'var(--text-primary)', padding: '8px 0' }} />)}
          </div>
          {row.attachments.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
              {row.attachments.map(a => (
                <span key={a.id} title={a.name}
                  style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '5px 9px', background: 'var(--c-gray-50)', border: '1px solid var(--border)', borderRadius: 8, maxWidth: 260 }}>
                  <i className={`ti ${fileIcon(a.type || '')}`} style={{ fontSize: 14, color: 'var(--accent)', flexShrink: 0 }} aria-hidden />
                  <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.name}</span>
                  <span style={{ fontSize: 10.5, color: 'var(--text-tertiary)', flexShrink: 0 }}>{fmtBytes(Number(a.size) || 0)}</span>
                </span>
              ))}
            </div>
          )}
        </div>
        <div style={{ padding: `0 ${PAGE_INSET}px 12px`, display: 'flex', flexDirection: 'column', gap: 10 }}>
          {parts ? (
            <div style={{ border: '1px solid var(--border-strong)', borderRadius: 9, overflow: 'hidden', background: 'var(--bg-surface)' }}>
              <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, padding: '4px 6px', background: 'var(--c-gray-50)', borderBottom: '1px solid var(--border)' }}>
                <Tool on={() => editor?.chain().focus().toggleBold().run()} active={editor?.isActive('bold')} title={t('bold')} icon="ti-bold" />
                <Tool on={() => editor?.chain().focus().toggleItalic().run()} active={editor?.isActive('italic')} title={t('italic')} icon="ti-italic" />
                <Tool on={() => editor?.chain().focus().toggleUnderline().run()} active={editor?.isActive('underline')} title={t('underline')} icon="ti-underline" />
                {sep}
                <Tool on={() => editor?.chain().focus().toggleBulletList().run()} active={editor?.isActive('bulletList')} title={t('bulletList')} icon="ti-list" />
                <Tool on={() => editor?.chain().focus().toggleOrderedList().run()} active={editor?.isActive('orderedList')} title={t('numberedList')} icon="ti-list-numbers" />
                <Tool on={() => { if (editor) shiftIndent(editor, -1); }} title={t('eml_fmt_outdent')} icon="ti-indent-decrease" />
                <Tool on={() => { if (editor) shiftIndent(editor, 1); }} title={t('eml_fmt_indent')} icon="ti-indent-increase" />
                {sep}
                {TEXT_COLORS.map(c => (
                  <button key={c} type="button" title={t('eml_fmt_color', { color: c })} aria-label={t('eml_fmt_color', { color: c })}
                    onMouseDown={e => { e.preventDefault(); editor?.chain().focus().setColor(c).run(); }}
                    style={{ width: 15, height: 15, borderRadius: '50%', background: c, border: '1px solid var(--border-strong)', cursor: 'pointer', padding: 0, margin: '0 1px' }} />
                ))}
                <Tool on={() => editor?.chain().focus().unsetColor().run()} title={t('eml_fmt_no_color')} icon="ti-ban" />
              </div>
              <div style={{ padding: '10px 12px', minHeight: 160 }}>
                <EditorContent editor={editor} />
              </div>
            </div>
          ) : (
            <div>
              <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginBottom: 6 }}>{t('ec_text_fixed')}</div>
              {frame(row.bodyHtml || '')}
            </div>
          )}
          {kept && (
            <div>
              <div style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--text-tertiary)', marginBottom: 5 }}>{t('ec_kept')}</div>
              {frame(kept)}
            </div>
          )}
        </div>
      </div>
      <div style={{ padding: `10px ${PAGE_INSET}px`, borderTop: '1px solid var(--border)', display: 'flex', flexDirection: 'column', gap: 8, flexShrink: 0 }}>
        {err && <div style={{ fontSize: 12, color: '#B42318' }}>{err}</div>}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <button onClick={() => { void save(); }} disabled={busy}
            style={{ padding: '7px 18px', borderRadius: 999, border: '1px solid', fontWeight: 700, fontSize: 12.5, cursor: busy ? 'wait' : 'pointer', ...menuPill(HOME, true) }}>
            {t('save')}
          </button>
          <button onClick={onCancel} disabled={busy} style={neutralPill}>{t('eml_sched_cancel')}</button>
          <span style={{ fontSize: 11.5, color: 'var(--text-tertiary)' }}>{t('ec_edit_hint')}</span>
        </div>
      </div>
    </div>
  );
}

function Reader({ row, onDone, onSaved }: { row: Row; onDone: () => void; onSaved: () => void }) {
  const { t } = useTranslation();
  const { uiLang, showToast } = useStore();
  const [full, setFull] = useState<Row | null>(null);
  const [gone, setGone] = useState(false);
  const [busy, setBusy] = useState(false);
  const [noting, setNoting] = useState(false);
  const [editing, setEditing] = useState(false);
  const [note, setNote] = useState('');
  const [err, setErr] = useState('');
  useEffect(() => {
    let dead = false;
    api.emailCheck(row.id)
      .then((d: any) => { if (dead) return; if (d?.ok) setFull(d.row); else setGone(true); })
      .catch((e: any) => { if (!dead) setErr(e?.message || t('ec_failed')); });
    return () => { dead = true; };
  }, [row.id, t]);

  const answer = async (call: () => Promise<{ ok: boolean; error?: string }>, done: string) => {
    setBusy(true); setErr('');
    try {
      const r = await call();
      if (r.ok === false) { setErr(r.error || t('ec_failed')); setBusy(false); return; }
      showToast(done);
      onDone();
    } catch (e: any) { setErr(e?.message || t('ec_failed')); setBusy(false); }
  };
  const download = async (att: { id: string; name: string }) => {
    const blob = await api.emailCheckFile(row.id, att.id).catch(() => null);
    if (!blob) { setErr(t('ec_failed')); return; }
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = att.name;
    document.body.appendChild(a); a.click();
    document.body.removeChild(a); URL.revokeObjectURL(url);
  };

  if (gone) return <div style={{ padding: 40, textAlign: 'center', fontSize: 13, color: 'var(--text-tertiary)' }}>{t('ec_gone')}</div>;
  if (editing && full) {
    return <EditForm row={full} onCancel={() => setEditing(false)} onSaved={fresh => { setFull(fresh); setEditing(false); onSaved(); }} />;
  }
  // The whole email once it has loaded, which after an edit is the saved version; until then what the list knows.
  const shown = full || row;
  return (
    <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
        <div style={{ padding: `12px ${PAGE_INSET}px`, borderBottom: '1px solid var(--border)', display: 'flex', flexDirection: 'column', gap: 3 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 4, overflowWrap: 'anywhere' }}>{shown.subject || '(No subject)'}</div>
          {head(t('ec_from'), shown.accountName ? `${shown.accountName} <${shown.accountEmail}>` : shown.accountEmail)}
          {head(t('to'), shown.to.join(', ') || '—')}
          {shown.cc.length > 0 && head(t('ec_cc'), shown.cc.join(', '))}
          {shown.bcc.length > 0 && head(t('ec_bcc'), shown.bcc.join(', '))}
          {head(t('ec_writer'), `${shown.writer || '—'} · ${fmtBkk(shown.askedAt * 1000, uiLang)}`)}
          {shown.editedByName && head(t('ec_editor'), `${shown.editedByName}${shown.editedAt ? ` · ${fmtBkk(shown.editedAt * 1000, uiLang)}` : ''}`)}
          {shown.targetLang && shown.targetLang !== shown.bodyLang && (
            <div style={{ fontSize: 11.5, color: 'var(--text-tertiary)', marginTop: 2 }}>
              <i className="ti ti-language" style={{ fontSize: 12 }} aria-hidden /> {t('ec_translated', { lang: t(`langNames.${shown.targetLang}` as any, { defaultValue: shown.targetLang }) })}
            </div>
          )}
          {shown.attachments.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
              {shown.attachments.map(a => (
                <button key={a.id} onClick={() => { void download(a); }} title={a.name}
                  style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '5px 9px', background: 'var(--c-gray-50)', border: '1px solid var(--border)', borderRadius: 8, maxWidth: 260, cursor: 'pointer' }}>
                  <i className={`ti ${fileIcon(a.type || '')}`} style={{ fontSize: 14, color: 'var(--accent)', flexShrink: 0 }} aria-hidden />
                  <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.name}</span>
                  <span style={{ fontSize: 10.5, color: 'var(--text-tertiary)', flexShrink: 0 }}>{fmtBytes(Number(a.size) || 0)}</span>
                  <i className="ti ti-download" style={{ fontSize: 13, color: 'var(--accent)', flexShrink: 0 }} aria-hidden />
                </button>
              ))}
            </div>
          )}
        </div>
        <div style={{ background: '#fff' }}>
          {full
            ? <HtmlFrame html={full.bodyHtml || ''} />
            : <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}><i className="ti ti-loader" style={{ fontSize: 22, color: 'var(--text-tertiary)' }} aria-hidden /></div>}
        </div>
      </div>
      <div style={{ padding: `10px ${PAGE_INSET}px`, borderTop: '1px solid var(--border)', display: 'flex', flexDirection: 'column', gap: 8, flexShrink: 0 }}>
        {noting && (
          <textarea value={note} onChange={e => setNote(e.target.value)} placeholder={t('ec_note_ph')} aria-label={t('ec_note_ph')} rows={3} autoFocus
            style={{ width: '100%', boxSizing: 'border-box', padding: '7px 10px', fontSize: 13, borderRadius: 8, border: '1px solid var(--border-strong)', background: 'var(--bg-surface)', color: 'var(--text-primary)', resize: 'vertical', fontFamily: 'inherit' }} />
        )}
        {err && <div style={{ fontSize: 12, color: '#B42318' }}>{err}</div>}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          {noting ? (
            <>
              <button onClick={() => { void answer(() => api.returnEmailCheck(row.id, note.trim()), t('ec_returned')); }} disabled={busy || !note.trim()}
                style={{ padding: '7px 16px', borderRadius: 999, border: '1px solid rgba(226,75,74,0.45)', background: 'transparent', color: '#E24B4A', fontWeight: 700, fontSize: 12.5, cursor: busy ? 'wait' : !note.trim() ? 'not-allowed' : 'pointer', opacity: !note.trim() ? 0.55 : 1 }}>
                {t('ec_send_back')}
              </button>
              <button onClick={() => { setNoting(false); setErr(''); }} disabled={busy} style={neutralPill}>
                {t('eml_sched_cancel')}
              </button>
            </>
          ) : (
            <>
              <button onClick={() => { void answer(() => api.approveEmailCheck(row.id), t('ec_approved')); }} disabled={busy || !full}
                style={{ padding: '7px 18px', borderRadius: 999, border: '1px solid', fontWeight: 700, fontSize: 12.5, cursor: busy ? 'wait' : !full ? 'not-allowed' : 'pointer', opacity: !full ? 0.55 : 1, ...menuPill(HOME, true) }}>
                {t('oe_approve')}
              </button>
              <button onClick={() => { setEditing(true); setErr(''); }} disabled={busy || !full}
                style={{ ...neutralPill, display: 'flex', alignItems: 'center', gap: 5, color: 'var(--text-primary)', border: '1px solid var(--border-strong)', cursor: !full ? 'not-allowed' : 'pointer', opacity: !full ? 0.55 : 1 }}>
                <i className="ti ti-pencil" style={{ fontSize: 13 }} aria-hidden />{t('edit')}
              </button>
              <button onClick={() => setNoting(true)} disabled={busy}
                style={{ padding: '7px 16px', borderRadius: 999, border: '1px solid rgba(226,75,74,0.45)', background: 'transparent', color: '#E24B4A', fontWeight: 700, fontSize: 12.5, cursor: 'pointer' }}>
                {t('ec_send_back')}
              </button>
            </>
          )}
          <span style={{ fontSize: 11.5, color: 'var(--text-tertiary)' }}>{noting ? t('ec_back_hint') : full?.editedByName ? t('ec_approve_edited_hint') : t('ec_approve_hint')}</span>
        </div>
      </div>
    </div>
  );
}

export default function EmailChecksView() {
  const { t } = useTranslation();
  const { uiLang } = useStore();
  const [searchParams] = useSearchParams();
  const [rows, setRows] = useState<Row[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(100);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [picked, setPicked] = useState('');
  const load = useCallback(async (p: number) => {
    setErr('');
    try {
      const r = await api.emailChecks(p);
      setRows(r.rows || []); setTotal(Number(r.total) || 0); setPage(Number(r.page) || p); setLimit(Number(r.limit) || 100);
    } catch (e: any) { setErr(e?.message || t('ec_failed')); } finally { setLoading(false); }
  }, [t]);
  useEffect(() => { void load(1); }, [load]);
  // The one opened from the red number's list (?focus=) until another is picked, else the newest. A later row of that list wins again.
  const focus = searchParams.get('focus') || '';
  useEffect(() => { setPicked(''); }, [focus]);
  const wanted = picked || focus;
  const open = rows.find(r => r.id === wanted) || rows[0];

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, background: 'var(--bg-surface)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 16px', ...pageToolbar(), background: 'var(--c-gray-50)', flexWrap: 'wrap' }}>
        <i className="ti ti-mail-pause" style={{ fontSize: 15, color: 'var(--text-secondary)' }} aria-hidden />
        <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{t('appr_arrived_email_check')}</span>
        {!loading && <span style={{ fontSize: 11.5, color: 'var(--text-tertiary)' }}>{total}</span>}
      </div>
      {err && <div style={{ fontSize: 12, color: '#E24B4A', background: '#FCEBEB', padding: '10px 16px' }}>{err}</div>}
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}><i className="ti ti-loader" style={{ fontSize: 24, color: 'var(--text-tertiary)' }} aria-hidden /></div>
      ) : rows.length === 0 ? (
        <div style={{ padding: 40, textAlign: 'center', fontSize: 13, color: 'var(--text-tertiary)' }}>{t('ec_empty')}</div>
      ) : (
        <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
          <div style={{ width: 320, flexShrink: 0, borderRight: '1px solid var(--border)', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <div style={{ flex: 1, overflowY: 'auto', background: rowTint(HOME) }}>
              {rows.map(r => (
                <button key={r.id} onClick={() => setPicked(r.id)}
                  style={{ display: 'block', width: '100%', textAlign: 'left', padding: '10px 14px', border: 'none', borderBottom: '1px solid var(--border)', background: open?.id === r.id ? 'var(--bg-active)' : 'transparent', cursor: 'pointer' }}>
                  <span style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {r.subject || '(No subject)'}
                    {r.attachments.length > 0 && <span style={{ marginLeft: 6, fontSize: 11, color: 'var(--text-tertiary)' }}><i className="ti ti-paperclip" aria-hidden /> {r.attachments.length}</span>}
                  </span>
                  <span style={{ display: 'block', fontSize: 11.5, color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginTop: 2 }}>{t('to')}: {r.to.join(', ') || '—'}</span>
                  <span style={{ display: 'block', fontSize: 11, color: 'var(--text-tertiary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginTop: 2 }}>{r.writer || '—'} · {fmtBkk(r.askedAt * 1000, uiLang)}</span>
                </button>
              ))}
            </div>
            {total > limit && <Pagination page={page} total={total} limit={limit} onPage={p => { void load(p); }} />}
          </div>
          {/* A key per email: a note half typed for one never carries over to the next, nor does an edit. A save reloads the list
              on its page, so a changed subject or address shows there too. */}
          {open && <Reader key={open.id} row={open} onDone={() => { setPicked(''); void load(1); }} onSaved={() => { void load(page); }} />}
        </div>
      )}
    </div>
  );
}
